import { z } from 'zod'

import { CaptureRefSchema } from './capture'
import { ModuleTypeEnum } from './module'
import { ModuleReferenceInfoSchema } from './module-reference'

// Event status enum
export enum EventStatusEnum {
  UPCOMING = 'UPCOMING',
  COMPLETED = 'COMPLETED',
  ON_HOLD = 'ON_HOLD',
  CANCELED = 'CANCELED',
}

export const EventAccessTypeEnum = z.enum([
  'PUBLIC',
  'EMAIL_RESTRICTED',
  'PASSWORD',
  'INTERNAL',
])
export type EventAccessType = z.infer<typeof EventAccessTypeEnum>

export const EventInviteeRoleEnum = z.enum(['ORGANIZER', 'ATTENDEE', 'SPEAKER'])
export type EventInviteeRole = z.infer<typeof EventInviteeRoleEnum>

export const EventInviteeStatusEnum = z.enum([
  'INVITED',
  'CONFIRMED',
  'DECLINED',
  'MAYBE',
])
export type EventInviteeStatus = z.infer<typeof EventInviteeStatusEnum>

export const EventExternalSourceProviderEnum = z.enum([
  'GOOGLE_CALENDAR',
  'OUTLOOK_CALENDAR',
])
export type EventExternalSourceProvider = z.infer<
  typeof EventExternalSourceProviderEnum
>

export const EventRsvpResponseEnum = z.enum(['YES', 'NO', 'MAYBE', 'WAITLIST'])
export type EventRsvpResponse = z.infer<typeof EventRsvpResponseEnum>

export type EventSessionType = string
export type EventAgendaItemType = string
export type EventSessionSpeakerRole = string
export type EventHostRole = string

export const EventRsvpQuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  type: z.enum(['SHORT_TEXT', 'LONG_TEXT', 'DROPDOWN', 'CHECKBOX', 'RADIO']),
  required: z.boolean().optional(),
  options: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
})
export type EventRsvpQuestion = z.infer<typeof EventRsvpQuestionSchema>

export const EventRsvpSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  requireApproval: z.boolean().default(false),
  allowGuests: z.boolean().default(false),
  maxGuests: z.number().int().min(0).optional(),
  capacity: z.number().int().min(1).optional(),
  closeAt: z.string().datetime().optional(),
  questions: z.array(EventRsvpQuestionSchema).default([]),
})
export type EventRsvpSettings = z.infer<typeof EventRsvpSettingsSchema>

export const EventPublicSettingsSchema = z.object({
  coverImageUrl: z.string().url().optional(),
  accentColor: z.string().optional(),
  heroLayout: z.enum(['CENTERED', 'SPLIT']).optional(),
  showOrgBranding: z.boolean().default(true),
  ctaLabel: z.string().optional(),
  summary: z.string().optional(),
})
export type EventPublicSettings = z.infer<typeof EventPublicSettingsSchema>

export const EventInviteeSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  role: EventInviteeRoleEnum.default('ATTENDEE'),
  status: EventInviteeStatusEnum.default('INVITED'),
  email: z.string().email().optional(),
  name: z.string().optional(),
  userId: z.string().uuid().nullable().optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  source: z.string().default('SCRAMBL'),
  responseStatus: z.string().nullable().optional(),
  emailNormalized: z.string().nullable().optional(),
  isOrganizer: z.boolean().default(false),
  metadata: z.record(z.string(), z.any()).nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventInvitee = z.infer<typeof EventInviteeSchema>

export const EventModuleLinkSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  moduleId: z.string().uuid(),
  isPrimary: z.boolean().default(false),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventModuleLink = z.infer<typeof EventModuleLinkSchema>

export const EventExternalSourceSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  provider: EventExternalSourceProviderEnum,
  externalEventId: z.string(),
  iCalUid: z.string().nullable().optional(),
  seriesExternalId: z.string(),
  occurrenceKey: z.string(),
  calendarId: z.string(),
  etag: z.string().nullable().optional(),
  htmlLink: z.string().nullable().optional(),
  organizerEmail: z.string().nullable().optional(),
  externalUpdatedAt: z.date().nullable().optional(),
  isRecurring: z.boolean().default(false),
  status: z.string().default('CONFIRMED'),
  rawPayload: z.record(z.string(), z.any()).or(z.array(z.any())).optional(),
  lastSeenAt: z.date().nullable().optional(),
  lastSyncedAt: z.date().nullable().optional(),
  canceledAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventExternalSource = z.infer<typeof EventExternalSourceSchema>

export const ExternalCalendarEventReferenceSchema = z.object({
  provider: EventExternalSourceProviderEnum,
  calendarId: z.string(),
  externalEventId: z.string(),
  iCalUid: z.string().nullable().optional(),
  recurringEventId: z.string().nullable().optional(),
  originalStartTime: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  etag: z.string().nullable().optional(),
  htmlLink: z.string().nullable().optional(),
  organizerEmail: z.string().email().nullable().optional(),
  title: z.string(),
  description: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  isBusy: z.boolean().default(true),
  isAllDay: z.boolean().default(false),
  start: z.string(),
  end: z.string().nullable().optional(),
  attendees: z
    .array(
      z.object({
        email: z.string().email(),
        name: z.string().nullable().optional(),
        responseStatus: z.string().nullable().optional(),
        organizer: z.boolean().optional(),
      }),
    )
    .default([]),
  rawPayload: z.record(z.string(), z.any()).or(z.array(z.any())).optional(),
})
export type ExternalCalendarEventReference = z.infer<
  typeof ExternalCalendarEventReferenceSchema
>

export const PromoteExternalCalendarEventInputSchema = z.object({
  externalEvent: ExternalCalendarEventReferenceSchema,
  primaryModuleLink: z
    .object({
      moduleType: z.nativeEnum(ModuleTypeEnum),
      moduleId: z.string().uuid(),
    })
    .nullable()
    .optional(),
  moduleLinks: z
    .array(
      z.object({
        moduleType: z.nativeEnum(ModuleTypeEnum),
        moduleId: z.string().uuid(),
        isPrimary: z.boolean().optional(),
      }),
    )
    .optional(),
})
export type PromoteExternalCalendarEventInput = z.infer<
  typeof PromoteExternalCalendarEventInputSchema
>

export const FindEventByExternalSourceInputSchema = z.object({
  provider: EventExternalSourceProviderEnum,
  calendarId: z.string().optional(),
  externalEventId: z.string(),
  iCalUid: z.string().nullable().optional(),
  recurringEventId: z.string().nullable().optional(),
  originalStartTime: z.string().nullable().optional(),
  start: z.string().optional(),
})
export type FindEventByExternalSourceInput = z.infer<
  typeof FindEventByExternalSourceInputSchema
>

/**
 * Record types that can own an event. Organization is an explicit link whose
 * moduleId is the event's own organizationId (same model projects use).
 */
export const EVENT_OWNER_MODULE_TYPES = [
  ModuleTypeEnum.ORGANIZATION,
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.DEAL,
  ModuleTypeEnum.MEMBER,
] as const
export type EventOwnerModuleType = (typeof EVENT_OWNER_MODULE_TYPES)[number]

export const UpdateEventModuleLinksInputSchema = z.object({
  primaryModuleLink: z
    .object({
      moduleType: z.nativeEnum(ModuleTypeEnum),
      moduleId: z.string().uuid(),
    })
    .nullable()
    .optional(),
  moduleLinks: z
    .array(
      z.object({
        moduleType: z.nativeEnum(ModuleTypeEnum),
        moduleId: z.string().uuid(),
        isPrimary: z.boolean().optional(),
      }),
    )
    .default([]),
})
export type UpdateEventModuleLinksInput = z.infer<
  typeof UpdateEventModuleLinksInputSchema
>

export const EventRsvpSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  response: EventRsvpResponseEnum,
  name: z.string().optional(),
  email: z.string().email().optional(),
  guests: z.number().int().min(0).optional(),
  answers: z.record(z.string(), z.any()).optional(),
  source: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventRsvp = z.infer<typeof EventRsvpSchema>

export const EventSessionSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  eventDayId: z.string().uuid().nullable().optional(),
  title: z.string(),
  description: z.string().nullable().optional(),
  type: z.string().default('CUSTOM'),
  startTime: z.date().nullable().optional(),
  endTime: z.date().nullable().optional(),
  location: z.string().nullable().optional(),
  track: z.string().nullable().optional(),
  capacity: z.number().int().nullable().optional(),
  imageKey: z.string().nullable().optional(),
  imageUrl: z.string().url().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventSession = z.infer<typeof EventSessionSchema>

export const EventAgendaItemSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  eventDayId: z.string().uuid().nullable().optional(),
  sessionId: z.string().uuid().nullable().optional(),
  title: z.string(),
  description: z.string().nullable().optional(),
  type: z.string().default('CUSTOM'),
  startTime: z.date(),
  endTime: z.date().nullable().optional(),
  order: z.number().int().default(0),
  imageKey: z.string().nullable().optional(),
  imageUrl: z.string().url().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventAgendaItem = z.infer<typeof EventAgendaItemSchema>

export const EventSpeakerSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  contactId: z.string().uuid().nullable().optional(),
  name: z.string(),
  title: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  bio: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  linkedinUrl: z.string().nullable().optional(),
  imageKey: z.string().nullable().optional(),
  imageUrl: z.string().url().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventSpeaker = z.infer<typeof EventSpeakerSchema>

export const EventDaySchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  date: z.date(),
  startTime: z.date(),
  endTime: z.date(),
  timezone: z.string(),
  label: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventDay = z.infer<typeof EventDaySchema>

export const EventHostSchema = z.object({
  id: z.string().uuid(),
  eventId: z.string().uuid(),
  organizationId: z.string().uuid(),
  contactId: z.string().uuid().nullable().optional(),
  name: z.string(),
  role: z.string().default('HOST'),
  title: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  bio: z.string().nullable().optional(),
  imageKey: z.string().nullable().optional(),
  imageUrl: z.string().url().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventHost = z.infer<typeof EventHostSchema>

export const EventSessionSpeakerSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  speakerId: z.string().uuid(),
  role: z.string().default('SPEAKER'),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventSessionSpeaker = z.infer<typeof EventSessionSpeakerSchema>

export const EventSessionHostSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string().uuid(),
  hostId: z.string().uuid(),
  role: z.string().default('HOST'),
  createdAt: z.date(),
  updatedAt: z.date(),
})
export type EventSessionHost = z.infer<typeof EventSessionHostSchema>

export interface EventOptionConfig {
  id: string
  name: string
  description?: string
}

export interface EventTypeConfig {
  id: string
  name: string
  color: string
  description?: string
}

export const DEFAULT_EVENT_TYPES: EventTypeConfig[] = [
  {
    id: 'MEETING',
    name: 'Meeting',
    color: '#3B82F6',
    description: 'Standard meetings and calls',
  },
  {
    id: 'WORKSHOP',
    name: 'Workshop',
    color: '#8B5CF6',
    description: 'Interactive sessions and working groups',
  },
  {
    id: 'TRAINING',
    name: 'Training',
    color: '#10B981',
    description: 'Learning sessions and enablement',
  },
  {
    id: 'DEMO',
    name: 'Demo',
    color: '#F59E0B',
    description: 'Product walkthroughs and demos',
  },
  {
    id: 'REVIEW_RETRO',
    name: 'Review/Retro',
    color: '#EF4444',
    description: 'Reviews, retrospectives, and recaps',
  },
  {
    id: 'COMPANY_EVENT',
    name: 'Company Event',
    color: '#EC4899',
    description: 'Org-wide events and gatherings',
  },
]

export const DEFAULT_EVENT_SESSION_TYPES: EventOptionConfig[] = [
  { id: 'KEYNOTE', name: 'Keynote' },
  { id: 'PANEL', name: 'Panel' },
  { id: 'WORKSHOP', name: 'Workshop' },
  { id: 'BREAKOUT', name: 'Breakout' },
  { id: 'FIRESIDE_CHAT', name: 'Fireside Chat' },
  { id: 'NETWORKING', name: 'Networking' },
  { id: 'CUSTOM', name: 'Custom' },
]

export const DEFAULT_EVENT_AGENDA_ITEM_TYPES: EventOptionConfig[] = [
  { id: 'SESSION', name: 'Session' },
  { id: 'BREAK', name: 'Break' },
  { id: 'NETWORKING', name: 'Networking' },
  { id: 'CHECK_IN', name: 'Check in' },
  { id: 'MEAL', name: 'Meal' },
  { id: 'ANNOUNCEMENT', name: 'Announcement' },
  { id: 'CUSTOM', name: 'Custom' },
]

export const DEFAULT_EVENT_SESSION_SPEAKER_ROLES: EventOptionConfig[] = [
  { id: 'SPEAKER', name: 'Speaker' },
  { id: 'PANELIST', name: 'Panelist' },
  { id: 'MODERATOR', name: 'Moderator' },
  { id: 'KEYNOTE', name: 'Keynote' },
]

export const DEFAULT_EVENT_HOST_ROLES: EventOptionConfig[] = [
  { id: 'HOST', name: 'Host' },
  { id: 'CO_HOST', name: 'Co-host' },
  { id: 'GUEST_HOST', name: 'Guest host' },
  { id: 'MC', name: 'MC' },
  { id: 'MODERATOR', name: 'Moderator' },
]

// Event status colors
export const EVENT_STATUS_COLORS: Record<EventStatusEnum, string> = {
  [EventStatusEnum.UPCOMING]: '#3b82f6',
  [EventStatusEnum.COMPLETED]: '#10b981',
  [EventStatusEnum.ON_HOLD]: '#f59e0b',
  [EventStatusEnum.CANCELED]: '#6b7280',
}

// Event status display names
export const EVENT_STATUS_DISPLAY_NAMES: Record<EventStatusEnum, string> = {
  [EventStatusEnum.UPCOMING]: 'Upcoming',
  [EventStatusEnum.COMPLETED]: 'Completed',
  [EventStatusEnum.ON_HOLD]: 'On Hold',
  [EventStatusEnum.CANCELED]: 'Canceled',
}

// Event sort field enum
export enum EventSortFieldEnum {
  TITLE = 'title',
  DESCRIPTION = 'description',
  STATUS = 'status',
  START_TIME = 'startTime',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

export const CreatorSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
})

// Event schema
export const EventSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  eventType: z.string().nullable().optional(),
  bannerImageKey: z.string().nullable().optional(),
  bannerImageUrl: z.string().url().optional(),
  slug: z.string().optional(),
  accessType: EventAccessTypeEnum.default('INTERNAL'),
  allowedEmails: z.array(z.string().email()).optional(),
  publicSettings: EventPublicSettingsSchema.optional(),
  rsvpSettings: EventRsvpSettingsSchema.optional(),
  status: z.string().default(EventStatusEnum.UPCOMING),
  startTime: z.date(),
  endTime: z.date().nullable().optional(),
  latitude: z.number(),
  longitude: z.number(),
  creatorId: z.string().uuid(),
  creator: CreatorSchema,
  visibility: z.string().default('public'),
  // Module linking (same pattern as Tasks)
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  externalSource: EventExternalSourceSchema.optional(),
  isExternallyManaged: z.boolean().optional(),
  moduleLinks: z.array(EventModuleLinkSchema).optional(),
  primaryModuleLink: EventModuleLinkSchema.nullable().optional(),
  // T1 — capture provenance (U7 badge). Server-managed, write-once at
  // ingestion; null for records created before T1 or outside a wired producer.
  captureId: z.string().uuid().nullable().optional(),
  capture: CaptureRefSchema.nullable().optional(),
  // Server-managed: resolved name/path of the primary link (the owner).
  ownerInfo: ModuleReferenceInfoSchema.nullable().optional(),
  // Server-managed: derived from calendar-sync external sources.
  isRecurring: z.boolean().optional(),
  recurringSeriesId: z.string().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
  customFields: z.record(z.string(), z.any()).optional(),
})

export type Event = z.infer<typeof EventSchema>

// Create Event schema
export const CreateEventSchema = EventSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  creatorId: true,
  longitude: true,
  latitude: true,
  ownerInfo: true,
  isRecurring: true,
  recurringSeriesId: true,
}).extend({
  endTime: z.date().optional(),
  customFields: z.record(z.string(), z.any()).optional(),
  moduleType: z.string().nullable().optional(),
  moduleId: z.string().uuid().nullable().optional(),
  password: z.string().optional(),
})

export type CreateEvent = z.infer<typeof CreateEventSchema>

// Update Event schema
export const UpdateEventSchema = CreateEventSchema.partial().extend({
  endTime: z.date().nullable().optional(),
  customFields: z.record(z.string(), z.any()).optional(),
})

export const CreateEventDaySchema = EventDaySchema.omit({
  id: true,
  eventId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
})
export type CreateEventDay = z.infer<typeof CreateEventDaySchema>

export const UpdateEventDaySchema = CreateEventDaySchema.partial()
export type UpdateEventDay = z.infer<typeof UpdateEventDaySchema>

export const CreateEventSessionSchema = EventSessionSchema.omit({
  id: true,
  eventId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  type: z.string().optional(),
})
export type CreateEventSession = z.infer<typeof CreateEventSessionSchema>

export const UpdateEventSessionSchema = CreateEventSessionSchema.partial()
export type UpdateEventSession = z.infer<typeof UpdateEventSessionSchema>

export const CreateEventAgendaItemSchema = EventAgendaItemSchema.omit({
  id: true,
  eventId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  type: z.string().optional(),
})
export type CreateEventAgendaItem = z.infer<typeof CreateEventAgendaItemSchema>

export const UpdateEventAgendaItemSchema = CreateEventAgendaItemSchema.partial()
export type UpdateEventAgendaItem = z.infer<typeof UpdateEventAgendaItemSchema>

export const CreateEventSpeakerSchema = EventSpeakerSchema.omit({
  id: true,
  eventId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
})
export type CreateEventSpeaker = z.infer<typeof CreateEventSpeakerSchema>

export const UpdateEventSpeakerSchema = CreateEventSpeakerSchema.partial()
export type UpdateEventSpeaker = z.infer<typeof UpdateEventSpeakerSchema>

export const CreateEventHostSchema = EventHostSchema.omit({
  id: true,
  eventId: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  role: z.string().optional(),
})
export type CreateEventHost = z.infer<typeof CreateEventHostSchema>

export const UpdateEventHostSchema = CreateEventHostSchema.partial()
export type UpdateEventHost = z.infer<typeof UpdateEventHostSchema>

export const CreateEventSessionSpeakerSchema = EventSessionSpeakerSchema.omit({
  id: true,
  sessionId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  role: z.string().optional(),
})
export type CreateEventSessionSpeaker = z.infer<
  typeof CreateEventSessionSpeakerSchema
>

export const CreateEventSessionHostSchema = EventSessionHostSchema.omit({
  id: true,
  sessionId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  role: z.string().optional(),
})
export type CreateEventSessionHost = z.infer<
  typeof CreateEventSessionHostSchema
>

export const EVENT_BANNER_SPECS = {
  recommendedWidth: 1600,
  recommendedHeight: 900,
  aspectRatio: '16:9',
  maxSizeMb: 10,
}

export const EVENT_SPEAKER_IMAGE_SPECS = {
  recommendedWidth: 800,
  recommendedHeight: 800,
  aspectRatio: '1:1',
  maxSizeMb: 5,
}

export const EVENT_AGENDA_IMAGE_SPECS = {
  recommendedWidth: 1200,
  recommendedHeight: 675,
  aspectRatio: '16:9',
  maxSizeMb: 5,
}

export type UpdateEvent = z.infer<typeof UpdateEventSchema>

// Event response schema
export const EventResponseSchema = z.object({
  data: EventSchema,
  message: z.string().optional(),
})

export type EventResponse = z.infer<typeof EventResponseSchema>

// Events response schema
export const EventsResponseSchema = z.object({
  data: z.array(EventSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type EventsResponse = z.infer<typeof EventsResponseSchema>

export const ValidateEventAccessDtoSchema = z.object({
  token: z.string().optional(),
  password: z.string().optional(),
  email: z.string().email().optional(),
})
export type ValidateEventAccessDto = z.infer<
  typeof ValidateEventAccessDtoSchema
>

export const EventRsvpSubmissionSchema = z.object({
  response: EventRsvpResponseEnum,
  name: z.string().optional(),
  email: z.string().email().optional(),
  guests: z.number().int().min(0).optional(),
  answers: z.record(z.string(), z.any()).optional(),
})
export type EventRsvpSubmission = z.infer<typeof EventRsvpSubmissionSchema>

export interface PublicEventResponse {
  id: string
  title: string
  slug?: string
  description?: string | null
  eventType?: string | null
  status: string
  startTime: Date
  endTime?: Date | null
  accessType: EventAccessType
  publicSettings?: EventPublicSettings
  rsvpSettings?: EventRsvpSettings
  requiresAccessValidation: boolean
  requiresPassword?: boolean
  requiresEmail?: boolean
  requiresAuth?: boolean
  organization?: {
    name: string
    logoUrl?: string | null
  }
}
